import { isValidEmail } from '@frameio/utils/validators';

// Email format: "Name <email@domain.com>"
const ANGLE_BRACKET_EMAIL_REGEX = /([^<]*<)?([^@\s>]+@[^@\s>]+\.[^@\s>]+)>?/g;
const DELIMITER_REGEXP = /[,;\n\r\s]+/;

export function parseEmails(input: string): string[] {
  if (!input || !input.trim()) return [];
  const parts = input.split(DELIMITER_REGEXP).filter(Boolean);

  const emails = new Set();

  parts.forEach((item) => {
    const angleMatches = item.matchAll(ANGLE_BRACKET_EMAIL_REGEX);
    for (const match of angleMatches) {
      if (match[2] && isValidEmail(match[2])) {
        emails.add(match[2].toLowerCase());
        return;
      }
    }

    if (isValidEmail(item)) {
      emails.add(item.toLowerCase());
    }
  });

  return Array.from(emails) as string[];
}

// Split on commas, semicolons, and newlines — but NOT spaces, so that
// "Name <email>" patterns stay intact for angle-bracket extraction.
const OUTER_DELIMITER_REGEXP = /[,;\n\r]+/;
// Secondary split on whitespace for plain tokens with no angle brackets.
const INNER_DELIMITER_REGEXP = /\s+/;

/**
 * Like parseEmails but also includes invalid/non-email parts so they can be
 * tokenized in an error state rather than silently dropped.
 * For "Name <email>" format, extracts the email portion (even if invalid).
 * For plain space-separated tokens with no angle brackets, each word is a
 * separate token (e.g. "hello world" → ["hello", "world"]).
 */
export function parseAllInputs(input: string): string[] {
  if (!input || !input.trim()) return [];

  const results = new Set<string>();

  // First pass: split on non-space delimiters so "Name <email>" stays whole.
  const outerParts = input.split(OUTER_DELIMITER_REGEXP).filter(Boolean);

  outerParts.forEach((part) => {
    const trimmed = part.trim();
    if (!trimmed) return;

    // If the part contains an angle-bracket email pattern, extract that email.
    const angleMatches = [...trimmed.matchAll(ANGLE_BRACKET_EMAIL_REGEX)];
    if (angleMatches.length > 0) {
      for (const match of angleMatches) {
        if (match[2]) {
          results.add(match[2].toLowerCase());
          return;
        }
      }
    }

    // No angle-bracket pattern — split on spaces and add each token as-is.
    trimmed
      .split(INNER_DELIMITER_REGEXP)
      .filter(Boolean)
      .forEach((token) => results.add(token.toLowerCase()));
  });

  return Array.from(results);
}
